/**
 * Por qué este turno sale lo que sale.
 *
 * Un servicio puede cobrar distinto según el día y la hora: se prende con
 * `enableDynamicPricing` y las franjas se cargan arrastrando sobre la grilla de
 * components/PricingCalendar.jsx, que las guarda en `priceVariations`.
 *
 * La página pública ya mostraba el precio nuevo, el de lista tachado y un
 * "(+50%)", pero no decía POR QUÉ. El que reserva un sábado a la noche veía que
 * le cobraban más que lo que decía la lista de servicios y no tenía forma de
 * saber si era el día, la hora o un error del sitio. Un precio que aparece sin
 * motivo se lee como un cargo escondido, y aparece justo en el paso donde ya
 * eligió todo y sólo falta poner el nombre: es el peor momento para sembrarle
 * una duda.
 *
 * Vive en lib/ y no adentro de BusinessPage.jsx por lo mismo que bloqueos.js
 * vive afuera de server.js: la decisión —qué franja le toca a este turno— es
 * pura. Recibe el servicio, la fecha y la hora, y no toca la red ni el DOM.
 */

/**
 * En plural porque la etiqueta describe una regla que se repite todas las
 * semanas, no el día puntual que la persona eligió: "sábados de 18 a 23" es la
 * franja, "sábado 9 de agosto" es su turno, y eso ya se lo dice el renglón de
 * la fecha justo arriba.
 */
const NOMBRES_DE_DIA = {
  monday: 'lunes',
  tuesday: 'martes',
  wednesday: 'miércoles',
  thursday: 'jueves',
  friday: 'viernes',
  saturday: 'sábados',
  sunday: 'domingos',
};

const DIAS_DE_SEMANA = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const FIN_DE_SEMANA = ['saturday', 'sunday'];

/** `HH:MM` a minutos desde la medianoche, que es como se comparan los rangos. */
const aMinutos = (hora) => {
  const [h, m] = String(hora ?? '').split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/**
 * Los días de una variación.
 *
 * Se leen las dos claves porque las dos existen en los datos: PricingCalendar
 * escribe `days` y `daysOfWeek` con el mismo contenido —una para dibujar la
 * grilla y otra para calcular el precio— y quedaron guardados servicios con una
 * sola de las dos. Mirando nada más `daysOfWeek`, una franja vieja no aplicaba
 * y el cliente pagaba el precio de lista.
 */
const diasDeLaVariacion = (variacion) => {
  const deUna = variacion?.daysOfWeek;
  if (Array.isArray(deUna) && deUna.length > 0) return deUna;
  return Array.isArray(variacion?.days) ? variacion.days : [];
};

/**
 * La franja que le toca a este turno, con el rango que enganchó, o `null` si
 * corre el precio de lista.
 *
 * Gana la primera que coincide, que es el orden en el que ya venía resolviendo
 * la página pública. Ojo: PricingCalendar las recorre al revés (gana la última)
 * para pintar la grilla, así que con dos franjas superpuestas el panel puede
 * mostrar un precio y cobrarse el otro. No se toca acá porque cambiarlo cambia
 * lo que se le cobra a la gente, no cómo se explica.
 */
export const buscarVariacion = (service, fecha, hora) => {
  if (!service?.enableDynamicPricing) return null;
  if (!Array.isArray(service.priceVariations) || service.priceVariations.length === 0) return null;

  const minutos = aMinutos(hora);
  if (minutos === null || !(fecha instanceof Date) || Number.isNaN(fecha.getTime())) return null;

  // Las claves de `daysOfWeek` son los nombres en inglés en minúscula, que es
  // como los escribe la grilla del panel.
  const dia = fecha.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

  for (const variacion of service.priceVariations) {
    if (!variacion || !Number.isFinite(Number(variacion.price))) continue;
    if (!diasDeLaVariacion(variacion).includes(dia)) continue;

    const rangos = Array.isArray(variacion.timeRanges) ? variacion.timeRanges : [];
    for (const rango of rangos) {
      const desde = aMinutos(rango?.start);
      const hasta = aMinutos(rango?.end);
      if (desde === null || hasta === null) continue;

      if (minutos >= desde && minutos <= hasta) {
        return { variacion, rango };
      }
    }
  }

  return null;
};

/**
 * Los días en castellano y agrupados.
 *
 * "lunes, martes, miércoles, jueves y viernes" es la misma información que "de
 * lunes a viernes" ocupando el triple, y en el renglón de abajo de un precio el
 * largo importa. Los tres atajos son los tres recortes que la gente configura
 * de verdad.
 */
export const describirDias = (dias) => {
  const unicos = [...new Set(dias)].filter((d) => NOMBRES_DE_DIA[d]);
  if (unicos.length === 0) return '';

  const tieneTodos = (lista) => lista.every((d) => unicos.includes(d));

  if (unicos.length === 7) return 'todos los días';
  if (unicos.length === 5 && tieneTodos(DIAS_DE_SEMANA)) return 'de lunes a viernes';
  if (unicos.length === 2 && tieneTodos(FIN_DE_SEMANA)) return 'sábados y domingos';

  // En el orden de la semana y no en el que estén guardados: "domingos y
  // sábados" se lee como un error de tipeo.
  const ordenados = Object.keys(NOMBRES_DE_DIA)
    .filter((d) => unicos.includes(d))
    .map((d) => NOMBRES_DE_DIA[d]);

  if (ordenados.length === 1) return ordenados[0];
  return `${ordenados.slice(0, -1).join(', ')} y ${ordenados[ordenados.length - 1]}`;
};

/**
 * Lo que se le cobra por este turno y por qué.
 *
 * Devuelve siempre la misma forma —con o sin franja— para que quien lo muestra
 * no tenga que preguntar dos veces si hay precio distinto.
 *
 * `motivo` no sale del `name` de la variación: la grilla las bautiza sola
 * ("Precio especial SÁB, DOM") y no hay dónde renombrarlas, así que ese texto
 * es jerga del panel y no una explicación. Se arma con lo único que se sabe con
 * certeza: si sale más o menos que el precio de lista, y cuándo rige.
 */
export const precioDelTurno = (service, fecha, hora) => {
  const base = Number(service?.price) || 0;
  const encontrada = buscarVariacion(service, fecha, hora);

  if (!encontrada) {
    return { precio: base, base, hayVariacion: false, motivo: '', cuando: '' };
  }

  const { variacion, rango } = encontrada;
  const precio = Math.round(Number(variacion.price));
  const dias = describirDias(diasDeLaVariacion(variacion));
  const cuando = dias ? `${dias}, de ${rango.start} a ${rango.end} hs` : `de ${rango.start} a ${rango.end} hs`;

  // Igual al de lista no es una variación digna de explicar: el negocio dejó la
  // franja cargada con el mismo monto y anunciarla como "precio especial" sería
  // ruido. Se responde como si no existiera.
  if (precio === base) {
    return { precio: base, base, hayVariacion: false, motivo: '', cuando: '' };
  }

  return {
    precio,
    base,
    hayVariacion: true,
    motivo: precio > base ? 'Precio de horario pico' : 'Precio promocional',
    cuando,
  };
};
